import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import ChooseClientPlaceholder from './ChooseClientPlaceholder';
import { extractErrorMessage } from '../utils/errorMessage';
import { Trans, useTranslation } from 'react-i18next';
import { currentLocale } from '../i18n';

interface Creative {
  id: number;
  advertiser_id: string;
  display_name: string;
  creative_type: 'IMAGE' | 'HTML5' | 'VIDEO';
  ads_creative_id: string | null;
  review_status: 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'UNKNOWN';
  click_url: string | null;
  has_file: boolean;  // True if file uploaded through our UI (stored in GCS/local)
  has_thumbnail: boolean;  // True if video has extracted thumbnail
  preview_url: string | null;  // External preview URL (for synced creatives)
  width: number | null;
  height: number | null;
  duration_seconds: number | null;
  platform_upload_status: 'pending' | 'uploading' | 'uploaded' | 'failed' | 'skipped' | null;
  is_locked?: boolean;  // True if creative belongs to a legacy EUR advertiser (read-only)
  created_at: string;
  updated_at: string;
}

const ITEMS_PER_PAGE = 20;

const CreativesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAnalyst = user?.role === 'analyst' || user?.role === 'agency_analyst';
  const { selectedAdvertiser, isAllClients } = useAdvertiser();
  const selectedAdvertiserId = selectedAdvertiser?.advertiser_id || '';
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredCreative, setHoveredCreative] = useState<Creative | null>(null);
  const [previewPosition, setPreviewPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [creativeToDelete, setCreativeToDelete] = useState<Creative | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Bulk archive modal state
  const [showBulkArchiveModal, setShowBulkArchiveModal] = useState(false);
  const [isBulkArchiving, setIsBulkArchiving] = useState(false);

  // Close modals on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showDeleteModal && !isDeleting) setShowDeleteModal(false);
        if (showBulkArchiveModal && !isBulkArchiving) setShowBulkArchiveModal(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showDeleteModal, isDeleting, showBulkArchiveModal, isBulkArchiving]);

  useEffect(() => {
    if (selectedAdvertiserId) {
      loadCreatives();
    }
  }, [selectedAdvertiserId]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, statusFilter, searchQuery, selectedAdvertiserId]);

  // Clear selection when advertiser changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [selectedAdvertiserId]);

  const loadCreatives = async () => {
    // No specific client selected (All Clients = '__all__') — the page shows the
    // ChooseClientPlaceholder; calling the API with '__all__' 403s for non-admins.
    if (!selectedAdvertiserId || isAllClients) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/creatives?advertiser_id=${selectedAdvertiserId}`);
      setCreatives(response.data.creatives || []);
    } catch (err: any) {
      setError(extractErrorMessage(err, t('creatives.loadError')));
    } finally {
      setLoading(false);
    }
  };

  const filteredCreatives = creatives
    .filter(c => typeFilter === 'all' || c.creative_type === typeFilter)
    .filter(c => statusFilter === 'all' || c.review_status === statusFilter)
    .filter(c => !searchQuery || c.display_name.toLowerCase().includes(searchQuery.toLowerCase()));

  // Pagination
  const totalPages = Math.ceil(filteredCreatives.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedCreatives = filteredCreatives.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const counts = {
    all: creatives.length,
    IMAGE: creatives.filter(c => c.creative_type === 'IMAGE').length,
    HTML5: creatives.filter(c => c.creative_type === 'HTML5').length,
    VIDEO: creatives.filter(c => c.creative_type === 'VIDEO').length,
  };

  // Selection handlers
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleEditSelected = () => {
    const ids = Array.from(selectedIds);
    navigate(`/creatives/${selectedAdvertiserId}/edit/${ids.join(',')}`);
  };

  const handleEditSingle = (creative: Creative) => {
    navigate(`/creatives/${selectedAdvertiserId}/edit/${creative.id}`);
  };

  const handleDeleteClick = (creative: Creative) => {
    setCreativeToDelete(creative);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!creativeToDelete) return;

    setIsDeleting(true);
    try {
      await api.delete(`/api/creatives/${creativeToDelete.id}`);
      // Remove from local state
      setCreatives(prev => prev.filter(c => c.id !== creativeToDelete.id));
      setShowDeleteModal(false);
      setCreativeToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete creative:', err);
      setError(extractErrorMessage(err, t('creatives.deleteError')));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkArchive = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setIsBulkArchiving(true);
    try {
      await api.post(`/api/creatives/bulk-archive`, { creative_ids: ids });
      // Remove archived from local state
      setCreatives(prev => prev.filter(c => !selectedIds.has(c.id)));
      setSelectedIds(new Set());
      setShowBulkArchiveModal(false);
    } catch (err: any) {
      console.error('Failed to archive creatives:', err);
      setError(extractErrorMessage(err, t('creatives.bulkArchiveError')));
    } finally {
      setIsBulkArchiving(false);
    }
  };

  const getStatusBadge = (status: string, platformStatus?: string | null) => {
    const config: { [key: string]: { label: string; className: string } } = {
      'PENDING_REVIEW': { label: t('creatives.statusPending'), className: 'status-badge pending' },
      'APPROVED': { label: t('creatives.statusApproved'), className: 'status-badge active' },
      'REJECTED': { label: t('creatives.statusRejected'), className: 'status-badge rejected' },
      'UNKNOWN': { label: t('creatives.statusUnknown'), className: 'status-badge' }
    };
    const s = config[status] || config['UNKNOWN'];
    const isUploading = platformStatus === 'pending' || platformStatus === 'uploading';
    return (
      <span className={s.className}>
        {s.label}
        {isUploading && (
          <svg className="upload-clock-icon" viewBox="0 0 24 24" fill="currentColor">
            <title>{t('creatives.uploading')}</title>
            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
          </svg>
        )}
      </span>
    );
  };

  const getPlatformStatusBadge = (status: string | null) => {
    if (status !== 'failed') return null;
    return (
      <span className="platform-badge failed" title={t('creatives.uploadFailed')}>
        <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 12, height: 12 }}>
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <span>{t('creatives.failed')}</span>
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const config: { [key: string]: { color: string; bg: string; icon: React.ReactNode; label: string } } = {
      'IMAGE': {
        color: '#0369a1',
        bg: 'rgba(14, 165, 233, 0.12)',
        label: t('creatives.typeImage'),
        icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
      },
      'HTML5': {
        color: '#7c3aed',
        bg: 'rgba(139, 92, 246, 0.12)',
        label: 'HTML5',
        icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>
      },
      'VIDEO': {
        color: '#dc2626',
        bg: 'rgba(239, 68, 68, 0.12)',
        label: t('creatives.typeVideo'),
        icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
      }
    };
    const c = config[type] || { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)', icon: null, label: type };
    return (
      <span className="type-badge-new" style={{ color: c.color, background: c.bg }}>
        {c.icon}
        <span>{c.label}</span>
      </span>
    );
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const formatDimensions = (w: number | null, h: number | null) => {
    if (!w || !h) return '—';
    return `${w}×${h}`;
  };

  // Get file URL through our proxy
  // Only show actual image for files uploaded through our UI (has_file=true)
  // Synced platform creatives don't have working preview URLs
  const getFileUrl = (creative: Creative) => {
    if (creative.has_file) {
      // File uploaded through our UI - serve from storage
      return `/api/creatives/${creative.id}/file`;
    }
    // Don't try to load external preview_url - it doesn't work
    return null;
  };

  // Get thumbnail URL for video preview in table
  const getThumbnailUrl = (creative: Creative) => {
    if (creative.creative_type === 'VIDEO' && creative.has_thumbnail) {
      return `/api/creatives/${creative.id}/thumbnail`;
    }
    return null;
  };

  const handlePreviewHover = (creative: Creative, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredCreative(creative);
    setPreviewPosition({
      x: rect.right + 16,
      y: rect.top
    });
  };

  const handlePreviewLeave = () => {
    setHoveredCreative(null);
    setPreviewPosition(null);
  };

  const renderPreviewPopup = () => {
    if (!hoveredCreative || !previewPosition) return null;

    const maxWidth = 320;
    const maxHeight = 400;
    const creativeW = hoveredCreative.width || 300;
    const creativeH = hoveredCreative.height || 250;

    // Calculate scaled dimensions maintaining aspect ratio
    const scale = Math.min(maxWidth / creativeW, maxHeight / creativeH, 1);
    const width = Math.round(creativeW * scale);
    const height = Math.round(creativeH * scale);

    // Adjust position to stay in viewport
    const adjustedY = Math.min(previewPosition.y, window.innerHeight - height - 100);

    // Determine what to show based on type and availability
    const fileUrl = getFileUrl(hoveredCreative);
    const hasImagePreview = fileUrl && hoveredCreative.creative_type === 'IMAGE';
    const hasHtml5Preview = hoveredCreative.has_file && hoveredCreative.creative_type === 'HTML5';
    const hasVideoPreview = fileUrl && hoveredCreative.creative_type === 'VIDEO';

    const renderPlaceholder = (type: string) => {
      const config: { [key: string]: { icon: React.ReactNode; label: string; className: string } } = {
        'IMAGE': {
          icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>,
          label: t('creatives.typeImage'),
          className: 'preview-placeholder-popup image'
        },
        'HTML5': {
          icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>,
          label: 'HTML5',
          className: 'preview-placeholder-popup html5'
        },
        'VIDEO': {
          icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>,
          label: t('creatives.typeVideo'),
          className: 'preview-placeholder-popup video'
        }
      };
      const c = config[type] || config['IMAGE'];
      const showDims = hoveredCreative.width && hoveredCreative.height;
      return (
        <div className={c.className}>
          {c.icon}
          <span className="placeholder-label">{c.label}</span>
          {showDims && <span className="placeholder-dims">{creativeW}×{creativeH}</span>}
        </div>
      );
    };

    return (
      <div
        className="creative-preview-popup"
        style={{
          position: 'fixed',
          left: previewPosition.x,
          top: Math.max(adjustedY, 20),
          zIndex: 1000,
          width,
          height
        }}
      >
        {hasImagePreview ? (
          <img
            src={fileUrl}
            alt={hoveredCreative.display_name}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : hasHtml5Preview ? (
          <iframe
            src={`/api/creatives/${hoveredCreative.id}/html5`}
            title={hoveredCreative.display_name}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: creativeW,
              height: creativeH,
              border: 'none',
              transform: `scale(${scale})`,
              transformOrigin: 'top left'
            }}
          />
        ) : hasVideoPreview ? (
          <video
            src={fileUrl}
            poster={hoveredCreative.has_thumbnail ? `/api/creatives/${hoveredCreative.id}/thumbnail` : undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#f5f5f5' }}
          />
        ) : (
          renderPlaceholder(hoveredCreative.creative_type)
        )}
      </div>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) pages.push(i);

      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }

    return (
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
        </button>

        {pages.map((page, i) => (
          typeof page === 'number' ? (
            <button
              key={i}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ) : (
            <span key={i} className="pagination-ellipsis">...</span>
          )
        ))}

        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
          </svg>
        </button>

        <span className="pagination-info">
          {t('creatives.pageRange', {
            from: startIndex + 1,
            to: Math.min(startIndex + ITEMS_PER_PAGE, filteredCreatives.length),
            total: filteredCreatives.length,
          })}
        </span>
      </div>
    );
  };

  if (isAllClients) return <ChooseClientPlaceholder pageKey="nav.creatives" />;

  return (
    <>
      {/* Header */}
      <header className="header">
        <div className="header-title">
          <h1>{t('creatives.title')}</h1>
          <p className="header-subtitle">{t('creatives.subtitle')}</p>
        </div>
        <div className="header-actions">
          {selectedIds.size > 0 ? (
            <>
              <span className="selection-count">{t('creatives.selectedCount', { count: selectedIds.size })}</span>
              {!isAnalyst && (
              <button className="btn btn-secondary" onClick={handleEditSelected}>
                <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
                {t('creatives.edit')}
              </button>
              )}
              {!isAnalyst && (
              <button className="btn btn-secondary btn-danger-outline" onClick={() => setShowBulkArchiveModal(true)}>
                <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
                  <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z"/>
                </svg>
                {t('creatives.archive')}
              </button>
              )}
              <button className="btn btn-secondary" onClick={() => setSelectedIds(new Set())}>
                <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
                {t('creatives.clear')}
              </button>
            </>
          ) : (
            <>
              {!isAnalyst && (
              <button className="btn btn-primary" onClick={() => navigate('/creatives/upload')}>
                <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
                {t('creatives.upload')}
              </button>
              )}
            </>
          )}
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className="alert alert-error">
          <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 20, height: 20 }}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </div>
      )}

      {/* Filters Bar */}
      <div className="filters-bar">
        <div className="filters-left">
          <div className="status-segmented">
            <button
              className={`status-segment ${typeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setTypeFilter('all')}
            >
              {t('creatives.filterAll')} <span className="segment-count">{counts.all}</span>
            </button>
            <button
              className={`status-segment ${typeFilter === 'IMAGE' ? 'active' : ''}`}
              onClick={() => setTypeFilter('IMAGE')}
            >
              {t('creatives.typeImage')} <span className="segment-count">{counts.IMAGE}</span>
            </button>
            <button
              className={`status-segment ${typeFilter === 'HTML5' ? 'active' : ''}`}
              onClick={() => setTypeFilter('HTML5')}
            >
              HTML5 <span className="segment-count">{counts.HTML5}</span>
            </button>
            <button
              className={`status-segment ${typeFilter === 'VIDEO' ? 'active' : ''}`}
              onClick={() => setTypeFilter('VIDEO')}
            >
              {t('creatives.typeVideo')} <span className="segment-count">{counts.VIDEO}</span>
            </button>
          </div>
        </div>
        <div className="filters-right">
          <select
            className="status-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">{t('creatives.allStatuses')}</option>
            <option value="APPROVED">{t('creatives.statusApproved')}</option>
            <option value="PENDING_REVIEW">{t('creatives.statusPending')}</option>
            <option value="REJECTED">{t('creatives.statusRejected')}</option>
          </select>
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder={t('creatives.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>{t('creatives.loading')}</p>
        </div>
      ) : filteredCreatives.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-state-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
            </svg>
          </div>
          <h3>{t('creatives.noneFoundTitle')}</h3>
          <p>{searchQuery ? t('creatives.tryOtherSearch') : t('creatives.noneForClient')}</p>
        </div>
      ) : (
        <>
          <div className="campaigns-table-card">
            <table className="creatives-table">
              <thead>
                <tr>
                  <th className="checkbox-cell"></th>
                  <th>{t('creatives.colPreview')}</th>
                  <th>{t('creatives.colName')}</th>
                  <th>{t('creatives.colType')}</th>
                  <th>{t('creatives.colSize')}</th>
                  <th>{t('creatives.colStatus')}</th>
                  <th>ID</th>
                  <th>{t('creatives.colCreated')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginatedCreatives.map(creative => {
                  const thumbUrl = getFileUrl(creative);
                  const videoThumbUrl = getThumbnailUrl(creative);
                  const showImageThumb = thumbUrl && creative.creative_type === 'IMAGE';
                  const showVideoThumb = !!videoThumbUrl;
                  const showThumb = showImageThumb || showVideoThumb;
                  const previewSrc = showVideoThumb ? videoThumbUrl : thumbUrl;
                  return (
                  <tr key={creative.id} className={selectedIds.has(creative.id) ? 'selected-row' : ''}>
                    <td className="checkbox-cell">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(creative.id)}
                        onChange={() => toggleSelect(creative.id)}
                        className="creative-checkbox"
                      />
                    </td>
                    <td>
                      <div
                        className="creative-preview-cell"
                        onMouseEnter={(e) => handlePreviewHover(creative, e)}
                        onMouseLeave={handlePreviewLeave}
                      >
                        {showThumb ? (
                          <img
                            src={previewSrc!}
                            alt=""
                            className="preview-thumb"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                              (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={`preview-placeholder ${creative.creative_type === 'VIDEO' ? 'video' : creative.creative_type === 'HTML5' ? 'html5' : 'image'} ${showThumb ? 'hidden' : ''}`}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            {creative.creative_type === 'VIDEO' ? (
                              <path d="M8 5v14l11-7z"/>
                            ) : creative.creative_type === 'HTML5' ? (
                              <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
                            ) : (
                              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                            )}
                          </svg>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="creative-name-cell">
                        {creative.is_locked && (
                          <span title={t('creatives.legacyReadOnly')} style={{ marginRight: 6, color: '#888' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: 'middle' }}>
                              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/>
                            </svg>
                          </span>
                        )}
                        <span className="name-text" style={creative.is_locked ? { color: '#888' } : undefined}>
                          {creative.display_name}
                        </span>
                      </div>
                    </td>
                    <td>{getTypeBadge(creative.creative_type)}</td>
                    <td className="dim-cell">{formatDimensions(creative.width, creative.height)}</td>
                    <td>
                      <div className="status-badges">
                        {getStatusBadge(creative.review_status, creative.platform_upload_status)}
                        {getPlatformStatusBadge(creative.platform_upload_status)}
                      </div>
                    </td>
                    <td className="id-cell">{creative.id}</td>
                    <td className="date-cell">{formatDate(creative.created_at)}</td>
                    <td>
                      <div className="actions-cell">
                        {!isAnalyst && !creative.is_locked && (
                        <button className="btn-icon" title={t('creatives.edit')} onClick={() => handleEditSingle(creative)}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                          </svg>
                        </button>
                        )}
                        {!isAnalyst && !creative.is_locked && (
                        <button className="btn-icon btn-icon-danger" title={t('creatives.archive')} onClick={() => handleDeleteClick(creative)}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                          </svg>
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </>
      )}

      {/* Preview Popup on Hover */}
      {renderPreviewPopup()}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && creativeToDelete && (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteModal(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-icon danger">
              <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </div>
            <h3>{t('creatives.archiveCreativeQ')}</h3>
            <p>
              <Trans i18nKey="creatives.willArchiveOne"
                values={{ name: creativeToDelete.display_name }} components={{ b: <strong /> }} />
              {creativeToDelete.ads_creative_id && (
                <>
                  <br /><br />
                  <span style={{ color: '#666', fontSize: '13px' }}>
                    {t('creatives.removedFromGroups')}
                  </span>
                </>
              )}
            </p>
            <div className="confirm-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                {t('creatives.cancel')}
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? t('creatives.archiving') : t('creatives.archive')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Archive Confirmation Modal */}
      {showBulkArchiveModal && (
        <div className="modal-overlay" onClick={() => !isBulkArchiving && setShowBulkArchiveModal(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-icon danger">
              <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z"/>
              </svg>
            </div>
            <h3>{t('creatives.archiveNQ', { count: selectedIds.size })}</h3>
            <p>
              <Trans i18nKey="creatives.willArchiveMany"
                values={{ count: selectedIds.size }} components={{ b: <strong /> }} />
              <br /><br />
              <span style={{ color: '#666', fontSize: '13px' }}>
                {t('creatives.bulkRemovedFromGroups')}
              </span>
            </p>
            <div className="confirm-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowBulkArchiveModal(false)}
                disabled={isBulkArchiving}
              >
                {t('creatives.cancel')}
              </button>
              <button
                className="btn btn-danger"
                onClick={handleBulkArchive}
                disabled={isBulkArchiving}
              >
                {isBulkArchiving ? t('creatives.archiving') : t('creatives.archiveN', { count: selectedIds.size })}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreativesPage;
